import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Text, Heading } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';
import axios from "axios";
import { normalizeEmail, isValidEmail } from '@/utils/formValidation';

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) {
      setError("Email is required.");
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    setIsLoading(true);

    try {
      const response = await axios.post(
        "/api/forgot-password",
        { email: normalizedEmail },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data?.success) {
        setEmail(normalizedEmail);
        setIsSuccess(true);
        setError("");
      } else {
        setError("User not registered. Please enter a registered email.");
        setIsSuccess(false);
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError("User not registered. Please enter a registered email.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background text-foreground flex flex-col justify-center items-center relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Logo Section - Top Left */}
      <div className="absolute top-6 left-6 md:top-8 md:left-8 z-10">
        <Link 
          to="/"
          className="transition-opacity hover:opacity-80"
        >
          <img
            src="/Logo.png"
            alt="Logo"
            className="h-8 md:h-10 w-auto object-contain drop-shadow-sm"
          />
        </Link>
      </div>
      
      <div className="flex justify-center items-center gap-12 max-w-7xl w-full flex-wrap px-6 py-12 md:flex-nowrap relative z-10">
        <Card className={cn(
          "flex flex-col w-full max-w-[480px] p-8 md:p-10",
          "bg-white border border-border/50",
          "shadow-xl shadow-primary/5",
          "transition-all duration-300",
          "hover:shadow-2xl hover:shadow-primary/10"
        )}>
          <CardContent className="w-full p-0 space-y-6">
            {/* Header */}
            <div className="space-y-3 mb-2">
              <Heading level="h1" className="text-4xl font-bold text-foreground tracking-tight">
                Forgot Password?
              </Heading>
              <Text size="base" variant="muted" className="text-muted-foreground leading-relaxed">
                {!isSuccess 
                  ? "Enter your email address and we'll send you a link to reset your password."
                  : "We've sent you a password reset link. Please check your email inbox."
                }
              </Text>
            </div>

            {!isSuccess ? (
              <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div className="relative group/email">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within/email:text-primary" />
                    <Input
                      type="email"
                      id="email"
                      placeholder="Enter your email"
                      aria-label="Email Address"
                      className="w-full pl-10 h-10 transition-all"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
                    <Text size="sm" variant="destructive" className="text-destructive">
                      {error}
                    </Text>
                  </div>
                )}

                <Button 
                  type="submit" 
                  variant="default"
                  size="default"
                  disabled={isLoading}
                  className="w-full h-11 font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                    borderColor: getColor('primary')
                  }}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="rounded-md bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 p-3">
                  <Text size="sm" className="text-green-800 dark:text-green-200">
                    Password reset link has been sent to your email. Please check your inbox.
                  </Text>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="pt-4 text-center border-t border-border">
              <Link 
                to="/login_user"
                className={cn(
                  "inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary",
                  "transition-colors underline-offset-4 hover:underline"
                )}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
        
        <div className="hidden lg:block flex-shrink-0">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl blur-2xl" />
            <img 
              className="relative w-full max-w-[520px] aspect-[0.72] object-cover rounded-2xl shadow-2xl"
              loading="lazy"
              src="./abcd.png"
              alt="Forgot password illustration"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
